import { useCallback, useEffect, useMemo, useState } from 'react'
import { OfficeHead } from '../ui/OfficeShell.js'
import { notify, notifyError } from '../ui/Toasts.js'
import {
  api,
  type Dacheingabe,
  type Dachsicht,
  type Rechtsform,
  type Dachverwalter,
  type TenantUser
} from '../net/apiClient.js'
import { fehlerText } from '../net/errors.js'
import { uuidv7 } from '../net/ids.js'
import { EINLADUNG_SPEICHER } from './Gemeinschaftseinladung.js'

/**
 * Die Kita-Gemeinschaft – ein gemeinsamer Auftritt, getrennte Betriebe.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Wofür dieser Bildschirm da ist
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Mehrere Inhaberinnen führen eine Kita unter einem Namen. Hier wird
 * verwaltet, was ihnen GEMEINSAM gehört: Name, Logo, Standortadresse,
 * Kontaktangaben, Rechtsform – und die Frage, wer dazugehört.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Was hier ausdrücklich NICHT steht
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Keine Kinder, keine Eltern, keine Rechnung, kein Betrag, keine IBAN und
 * kein Abo eines anderen Betriebs. Nicht ausgeblendet, sondern nicht
 * vorhanden: Der Server liefert diese Angaben in dieser Ansicht nicht, und es
 * gibt keine Route, über die sie zu holen wären.
 *
 * Die Liste der Betriebe nennt deshalb Namen, Ort und Stand – nicht mehr.
 * Wer in einem Bereich etwas sehen will, braucht dort eine Berechtigung, und
 * die vergibt jede Inhaberin für ihren Bereich selbst.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Zwei Einladungen, die nicht dasselbe sind
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Das ist der Punkt, an dem eine Oberfläche Geld kostet, wenn sie
 * undeutlich ist:
 *
 *   Mitarbeiterin  arbeitet IN einem Bereich. Kein eigener Bereich, kein
 *                  eigenes Abo. Sie wird unter «Team» eingeladen, wie immer.
 *
 *   Inhaberin      führt ihren EIGENEN Bereich: eigene Kinder, eigene
 *                  Rechnungen, eigene IBAN, eigenes Abo. Sie wird hier
 *                  eingeladen und entscheidet selbst.
 *
 * Deshalb stehen beide Wege sichtbar nebeneinander, mit dem Unterschied im
 * Satz – und nicht als zwei Knöpfe, die gleich aussehen.
 */

const LEER: Dacheingabe = {
  name: '',
  rechtsform: 'selbstaendig',
  street: '',
  zip: '',
  city: '',
  email: '',
  phone: ''
}

const RECHTSFORM_TEXT: Record<Rechtsform, string> = {
  gemeinsame_firma: 'Eine gemeinsame Firma – alle Inhaberinnen gehören zur selben Rechtseinheit',
  selbstaendig: 'Rechtlich selbstständige Inhaberinnen unter gemeinsamem Namen'
}

/**
 * Was die Rechtsform für die Rechnung bedeutet.
 *
 * Steht hier, weil es die eine Frage ist, die später Geld kostet. Ein
 * Betrieb, der sie falsch beantwortet, stellt Rechnungen mit einem
 * Rechnungssteller, der nicht zum Konto passt – und solche Zahlungen kommen
 * zurück.
 */
const RECHTSFORM_FOLGE: Record<Rechtsform, string> = {
  gemeinsame_firma:
    'Auf der Rechnung steht der Kita-Name im Briefkopf. Rechnungssteller, UID und ' +
    'Konto bleiben die des jeweiligen Bereichs – prüfe mit deiner Treuhand, dass ' +
    'das hinterlegte Konto der Gesellschaft gehört.',
  selbstaendig:
    'Auf der Rechnung steht der Kita-Name im Briefkopf, darunter die rechtlich ' +
    'verantwortliche Firma des Bereichs. Konto und UID sind ihre eigenen.'
}

function chTag(iso: string): string {
  const t = iso.slice(0, 10).split('-')
  return t.length === 3 ? `${t[2]}.${t[1]}.${t[0]}` : iso
}

export function OfficeGemeinschaft({
  session
}: {
  session: { role: string; userId?: string }
}): JSX.Element {
  const [sicht, setSicht] = useState<Dachsicht | null>(null)
  const [fehler, setFehler] = useState('')
  const [laeuft, setLaeuft] = useState<string | null>(null)
  const [form, setForm] = useState<Dacheingabe>(LEER)
  const [anlegen, setAnlegen] = useState(false)
  const [einladung, setEinladung] = useState({ name: '', email: '' })
  const [team, setTeam] = useState<TenantUser[]>([])
  const [logoStand, setLogoStand] = useState(() => String(Date.now()))
  const [austrittFrage, setAustrittFrage] = useState(false)
  /*
    Eine offene Einladung, die dieser Browser mitgebracht hat.

    Eine Inhaberin mit bestehendem Betrieb klickt den Link in ihrer Mail,
    meldet sich an und landet hier. Der Schlüssel liegt dann im
    Sitzungsspeicher (siehe `Gemeinschaftseinladung`), und sie muss ihn nicht
    noch einmal suchen. Fehlt er, öffnet sie den Link erneut – nichts ist
    verloren.
  */
  const [offeneEinladung, setOffeneEinladung] = useState<{
    token: string
    kita: string
  } | null>(null)

  const wartet = (was: string): boolean => laeuft === was
  const irgendwas = laeuft !== null

  const laden = useCallback(async () => {
    try {
      const s = await api.dach()
      setSicht(s)
      if (s.dach) {
        setForm({
          name: s.dach.name,
          rechtsform: s.dach.rechtsform,
          street: s.dach.street,
          zip: s.dach.zip,
          city: s.dach.city,
          email: s.dach.email,
          phone: s.dach.phone
        })
      }
      setFehler('')
    } catch (e) {
      setFehler(fehlerText(e))
    }
  }, [])

  useEffect(() => {
    void laden()
  }, [laden])

  useEffect(() => {
    if (sicht?.dach) return
    let token = ''
    try {
      token = window.sessionStorage.getItem(EINLADUNG_SPEICHER) ?? ''
    } catch {
      /* Gesperrter Speicher: dann gibt es dieses Angebot eben nicht. */
    }
    if (!token) return
    let weg = false
    void (async () => {
      try {
        const { einladung } = await api.dachEinladungAnsehen(token)
        if (!weg) setOffeneEinladung({ token, kita: einladung.kita })
      } catch {
        /* Abgelaufen oder zurückgezogen – dann wird nichts angeboten. */
        try {
          window.sessionStorage.removeItem(EINLADUNG_SPEICHER)
        } catch {
          /* egal */
        }
      }
    })()
    return () => {
      weg = true
    }
  }, [sicht?.dach])

  /*
    Die Teamliste wird nur geholt, wenn sie gebraucht wird – für die Frage,
    wen man zur Dachverwaltung berechtigen kann. Sie kommt aus dem eigenen
    Betrieb; fremde Benutzer stehen dort nicht und lassen sich auch nicht
    benennen.
  */
  useEffect(() => {
    if (!sicht?.darfVerwalten) return
    let weg = false
    void (async () => {
      try {
        const { users } = await api.listUsers()
        if (!weg) setTeam(users)
      } catch {
        /* Ohne Liste gibt es das Auswahlfeld nicht. Kein Grund für eine Meldung. */
      }
    })()
    return () => {
      weg = true
    }
  }, [sicht?.darfVerwalten])

  const vollstaendig = useMemo(
    () => form.name.trim() !== '' && form.street.trim() !== '' && form.zip.trim() !== '' && form.city.trim() !== '',
    [form]
  )

  async function tun<T>(was: string, fn: () => Promise<T>, meldung?: string): Promise<void> {
    setLaeuft(was)
    try {
      await fn()
      if (meldung) notify.ok(meldung)
      await laden()
    } catch (e) {
      notifyError(e)
    } finally {
      setLaeuft(null)
    }
  }

  if (fehler) {
    return (
      <>
        <OfficeHead titel="Kita-Gemeinschaft" />
        <p className="o-warn-text">{fehler}</p>
      </>
    )
  }
  if (!sicht) {
    return (
      <>
        <OfficeHead titel="Kita-Gemeinschaft" />
        <p className="o-soft">Einen Moment …</p>
      </>
    )
  }

  /* ─────────────────────────────────────────────────────────────────────────
     Noch keine Gemeinschaft – der Einstieg
     ───────────────────────────────────────────────────────────────────────── */
  if (!sicht.dach) {
    return (
      <>
        <OfficeHead
          titel="Kita-Gemeinschaft"
          unter="Eine Kita. Mehrere Inhaberinnen. Jeder Bereich für sich."
        />
        {offeneEinladung && (
          <section className="o-card" id="gem-offene-einladung">
            <div className="o-pad">
              <h2>{offeneEinladung.kita} hat dich eingeladen</h2>
              <p className="o-soft small">
                Dein Betrieb tritt der Gemeinschaft bei. Deine Kunden, Rechnungen und
                Zahlungen bleiben unberührt, dein Abo bleibt dein eigenes. Künftige
                Rechnungen tragen den gemeinsamen Briefkopf.
              </p>
              <div className="office-actions">
                <button
                  type="button"
                  className="o-btn o-btn-primary"
                  id="gem-einladung-annehmen"
                  disabled={irgendwas || session.role !== 'superuser'}
                  onClick={() =>
                    void tun(
                      'beitreten',
                      async () => {
                        await api.dachBeitreten(offeneEinladung.token, uuidv7())
                        try {
                          window.sessionStorage.removeItem(EINLADUNG_SPEICHER)
                        } catch {
                          /* egal */
                        }
                        setOffeneEinladung(null)
                      },
                      'Dein Betrieb gehört jetzt zur Gemeinschaft.'
                    )
                  }
                >
                  {wartet('beitreten') ? 'Wird verbunden …' : 'Einladung annehmen'}
                </button>
              </div>
            </div>
          </section>
        )}

        <section className="o-card" id="gem-einstieg">
          <div className="o-pad">
            <p>
              Führt ihr eure Kita gemeinsam, während jede Inhaberin ihre eigenen Kinder
              betreut und ihre eigenen Rechnungen stellt? Dann richte hier den
              gemeinsamen Auftritt ein: <strong>ein Kita-Name, ein Logo, eine Adresse</strong>.
            </p>
            <p className="o-soft small">
              Jede Inhaberin behält ihren eigenen geschützten Bereich mit eigenen
              Kindern, Eltern, Rechnungen, eigener IBAN und eigenem Abo. Niemand sieht
              die Daten einer anderen – auch die Kita-Verwaltung nicht.
            </p>
            {!anlegen ? (
              <div className="office-actions">
                <button
                  type="button"
                  className="o-btn o-btn-primary"
                  id="gem-anlegen-start"
                  disabled={session.role !== 'superuser'}
                  onClick={() => setAnlegen(true)}
                >
                  Kita-Gemeinschaft einrichten
                </button>
                {session.role !== 'superuser' && (
                  <span className="o-soft small">
                    Das richtet die Inhaberin deines Betriebs ein.
                  </span>
                )}
              </div>
            ) : (
              <Stammdaten
                form={form}
                setForm={setForm}
                vollstaendig={vollstaendig}
                knopf="Gemeinschaft anlegen"
                id="gem-anlegen"
                wartet={wartet('anlegen')}
                gesperrt={irgendwas}
                aufSenden={() =>
                  void tun(
                    'anlegen',
                    () => api.dachAnlegen(form, uuidv7()),
                    'Die Gemeinschaft ist eingerichtet.'
                  )
                }
                aufAbbrechen={() => setAnlegen(false)}
              />
            )}
          </div>
        </section>
      </>
    )
  }

  const dach = sicht.dach
  const darf = sicht.darfVerwalten === true
  const aktive = (sicht.mitglieder ?? []).filter((m) => m.status === 'aktiv')
  const frueher = (sicht.mitglieder ?? []).filter((m) => m.status === 'ausgetreten')
  const offeneEinladungen = (sicht.einladungen ?? []).filter((e) => e.status === 'offen')

  /* ─────────────────────────────────────────────────────────────────────────
     Die Gemeinschaft
     ───────────────────────────────────────────────────────────────────────── */
  return (
    <>
      <OfficeHead
        titel={dach.name}
        unter={`${aktive.length === 1 ? '1 Inhaberbereich' : `${aktive.length} Inhaberbereiche`} · ${dach.city}`}
      />

      {/* Was auf jeder Rechnung dieser Kita steht. */}
      <section className="o-card" id="gem-briefkopf">
        <div className="o-pad">
          <h2>Gemeinsamer Briefkopf</h2>
          <p className="o-soft small">
            Diese Angaben stehen im Briefkopf jeder Rechnung aller Inhaberbereiche.
            Rechnungssteller, UID und Bankverbindung bleiben davon unberührt – die
            gehören dem einzelnen Bereich und werden dort gepflegt.
          </p>

          <div className="o-grid o-grid-2" id="gem-logo-zeile">
            <div>
              {dach.logoKey ? (
                <img
                  src={api.dachLogoUrl(logoStand)}
                  alt={`Logo ${dach.name}`}
                  id="gem-logo"
                  style={{ maxHeight: '64px', maxWidth: '220px' }}
                />
              ) : (
                <p className="o-soft small" id="gem-logo-fehlt">
                  Noch kein gemeinsames Logo. Ohne Logo steht der Kita-Name als Wortmarke
                  im Briefkopf.
                </p>
              )}
            </div>
            {darf && (
              <div className="office-actions">
                <button
                  type="button"
                  className="o-btn"
                  id="gem-logo-uebernehmen"
                  disabled={irgendwas}
                  onClick={() =>
                    void tun(
                      'logo',
                      async () => {
                        await api.dachLogoUebernehmen(uuidv7())
                        setLogoStand(String(Date.now()))
                      },
                      'Das Logo gilt jetzt für die ganze Kita.'
                    )
                  }
                >
                  {wartet('logo') ? 'Wird übernommen …' : 'Logo meines Betriebs übernehmen'}
                </button>
                {dach.logoKey && (
                  <button
                    type="button"
                    className="o-btn o-btn-ghost"
                    id="gem-logo-entfernen"
                    disabled={irgendwas}
                    onClick={() =>
                      void tun(
                        'logoweg',
                        () => api.dachLogoEntfernen(uuidv7()),
                        'Das gemeinsame Logo ist entfernt.'
                      )
                    }
                  >
                    Entfernen
                  </button>
                )}
              </div>
            )}
          </div>

          {darf ? (
            <Stammdaten
              form={form}
              setForm={setForm}
              vollstaendig={vollstaendig}
              knopf="Speichern"
              id="gem-stammdaten"
              wartet={wartet('speichern')}
              gesperrt={irgendwas}
              aufSenden={() =>
                void tun(
                  'speichern',
                  () => api.dachAendern(form, uuidv7()),
                  'Gespeichert. Künftige Rechnungen tragen diese Angaben.'
                )
              }
            />
          ) : (
            <dl className="o-dl" id="gem-stammdaten-lesen">
              <dt>Kita</dt>
              <dd>{dach.name}</dd>
              <dt>Adresse</dt>
              <dd>
                {dach.street}, {dach.zip} {dach.city}
              </dd>
              <dt>Organisationsform</dt>
              <dd>{RECHTSFORM_TEXT[dach.rechtsform]}</dd>
            </dl>
          )}

          <p className="o-soft small" id="gem-rechtsform-folge">
            {RECHTSFORM_FOLGE[dach.rechtsform]}
          </p>
          <p className="o-soft small" id="gem-unveraenderlich">
            Bereits ausgestellte Rechnungen ändern sich nicht mehr: Jede trägt den
            Briefkopf und die Zahlungsangaben, die beim Abschliessen galten.
          </p>
        </div>
      </section>

      {/* Wer dazugehört. */}
      <section className="o-card" id="gem-mitglieder">
        <div className="o-pad">
          <h2>Inhaberbereiche</h2>
          <div className="o-tabelle">
            <table className="o-data" id="gem-mitgliederliste">
              <thead>
                <tr>
                  <th>Bereich</th>
                  <th>Dabei seit</th>
                  <th>Stand</th>
                </tr>
              </thead>
              <tbody>
                {aktive.map((m) => (
                  <tr key={m.tenantId} data-mitglied={m.tenantId}>
                    <td>
                      {m.name}
                      {m.eigener && <span className="o-soft small"> · dein Bereich</span>}
                    </td>
                    <td>{chTag(m.beigetretenAm)}</td>
                    <td>
                      <span className="o-badge o-badge-ok">aktiv</span>
                    </td>
                  </tr>
                ))}
                {frueher.map((m) => (
                  <tr key={m.tenantId} data-mitglied={m.tenantId}>
                    <td className="o-soft">{m.name}</td>
                    <td className="o-soft">{chTag(m.beigetretenAm)}</td>
                    <td>
                      <span className="o-badge o-badge-neutral">
                        ausgetreten{m.verlassenAm ? ` am ${chTag(m.verlassenAm)}` : ''}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="o-soft small">
            Diese Liste nennt Namen und Stand. Kinder, Rechnungen, Zahlungen und
            Bankverbindungen eines Bereichs sind hier nicht zu sehen und nicht
            abrufbar.
          </p>
        </div>
      </section>

      {/* Einladen – und der Unterschied, auf den es ankommt. */}
      {darf && (
        <section className="o-card" id="gem-einladen">
          <div className="o-pad">
            <h2>Inhaberin einladen</h2>
            <p className="o-soft small">
              Eine Inhaberin führt ihren <strong>eigenen Bereich</strong>: eigene Kinder,
              eigene Rechnungen, eigene IBAN, eigenes Abo ab CHF 19.90 im Monat. Sie
              wählt ihr Paket selbst. Die Einladung allein kostet nichts und legt nichts
              an.
            </p>
            <p className="o-soft small" id="gem-unterschied">
              Suchst du eine <strong>Mitarbeiterin</strong> für deinen eigenen Bereich? Die
              lädst du unter «Team» ein – ohne eigenen Bereich und ohne zusätzliches Abo,
              im Rahmen deines Pakets.
            </p>

            <div className="o-grid o-grid-2">
              <label className="o-feld">
                <span>Name</span>
                <input
                  id="gem-einladung-name"
                  autoComplete="off"
                  value={einladung.name}
                  onChange={(e) => setEinladung({ ...einladung, name: e.target.value })}
                />
              </label>
              <label className="o-feld">
                <span>E-Mail</span>
                <input
                  id="gem-einladung-email"
                  type="email"
                  autoComplete="off"
                  value={einladung.email}
                  onChange={(e) => setEinladung({ ...einladung, email: e.target.value })}
                />
              </label>
            </div>
            <div className="office-actions">
              <button
                type="button"
                className="o-btn o-btn-primary"
                id="gem-einladung-senden"
                disabled={
                  irgendwas ||
                  einladung.name.trim().length < 2 ||
                  !einladung.email.includes('@')
                }
                onClick={() =>
                  void tun(
                    'einladen',
                    async () => {
                      await api.dachEinladen(
                        { name: einladung.name.trim(), email: einladung.email.trim() },
                        uuidv7()
                      )
                      setEinladung({ name: '', email: '' })
                    },
                    'Die Einladung ist unterwegs.'
                  )
                }
              >
                {wartet('einladen') ? 'Wird gesendet …' : 'Einladung senden'}
              </button>
            </div>

            {offeneEinladungen.length > 0 && (
              <div className="o-tabelle">
                <table className="o-data" id="gem-einladungsliste">
                  <thead>
                    <tr>
                      <th>Eingeladen</th>
                      <th>Gültig bis</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {offeneEinladungen.map((e) => (
                      <tr key={e.id} data-einladung={e.id}>
                        <td>
                          {e.name}
                          <span className="o-soft small"> · {e.email}</span>
                        </td>
                        <td>{chTag(e.gueltigBis)}</td>
                        <td>
                          <button
                            type="button"
                            className="o-btn o-btn-ghost"
                            id={`gem-einladung-weg-${e.id}`}
                            disabled={irgendwas}
                            onClick={() =>
                              void tun(
                                `weg-${e.id}`,
                                () => api.dachEinladungZurueckziehen(e.id, uuidv7()),
                                'Die Einladung ist zurückgezogen.'
                              )
                            }
                          >
                            Zurückziehen
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Wer die Gemeinschaft pflegen darf. */}
      {darf && (
        <section className="o-card" id="gem-verwalter">
          <div className="o-pad">
            <h2>Berechtigt für die Kita-Verwaltung</h2>
            <p className="o-soft small">
              Diese Personen pflegen den gemeinsamen Auftritt und laden Inhaberinnen ein.
              Sie erhalten dadurch <strong>keinen</strong> Zugang zu einem anderen
              Inhaberbereich.
            </p>
            <ul className="o-liste" id="gem-verwalterliste">
              {(sicht.verwalter ?? []).map((v: Dachverwalter) => (
                <li key={v.userId} data-verwalter={v.userId}>
                  {v.name}
                  <span className="o-soft small"> · {v.email}</span>
                  {!v.eigener && (
                    <button
                      type="button"
                      className="o-btn o-btn-ghost"
                      id={`gem-verwalter-weg-${v.userId}`}
                      disabled={irgendwas}
                      onClick={() =>
                        void tun(
                          `vweg-${v.userId}`,
                          () => api.dachVerwalterEntfernen(v.userId, uuidv7()),
                          'Die Berechtigung ist entfernt.'
                        )
                      }
                    >
                      Entfernen
                    </button>
                  )}
                </li>
              ))}
            </ul>

            {team.length > 0 && (
              <div className="office-actions">
                <select id="gem-verwalter-wahl" defaultValue="" disabled={irgendwas}>
                  <option value="">Person aus meinem Betrieb wählen …</option>
                  {team
                    .filter((u) => !(sicht.verwalter ?? []).some((v) => v.userId === u.userId))
                    .map((u) => (
                      <option key={u.userId} value={u.userId}>
                        {u.name} · {u.email}
                      </option>
                    ))}
                </select>
                <button
                  type="button"
                  className="o-btn"
                  id="gem-verwalter-dazu"
                  disabled={irgendwas}
                  onClick={() => {
                    const feld = document.getElementById(
                      'gem-verwalter-wahl'
                    ) as HTMLSelectElement | null
                    const id = feld?.value ?? ''
                    if (!id) {
                      notify.ok('Bitte zuerst eine Person wählen.')
                      return
                    }
                    void tun(
                      'vdazu',
                      () => api.dachVerwalterHinzufuegen(id, uuidv7()),
                      'Die Berechtigung ist erteilt.'
                    )
                  }}
                >
                  Berechtigen
                </button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Austreten. */}
      <section className="o-card" id="gem-austritt">
        <div className="o-pad">
          <h2>Aus der Gemeinschaft austreten</h2>
          <p className="o-soft small">
            Dein Bereich bleibt vollständig bestehen – mit Kindern, Eltern, Rechnungen,
            Zahlungen und deinem Abo. Was endet, ist der gemeinsame Auftritt: Künftige
            Rechnungen tragen wieder deinen eigenen Briefkopf. Bereits ausgestellte
            Rechnungen bleiben unverändert.
          </p>
          <p className="o-soft small">
            Klärt vorher, wem der gemeinsame Kita-Name, das Logo und die Adresse
            gehören – das ist eine Frage an euren Vertrag und nicht an die Software.
          </p>
          {!austrittFrage ? (
            <div className="office-actions">
              <button
                type="button"
                className="o-btn o-btn-ghost"
                id="gem-austritt-start"
                disabled={irgendwas || session.role !== 'superuser'}
                onClick={() => setAustrittFrage(true)}
              >
                Austreten
              </button>
            </div>
          ) : (
            <div className="office-actions" id="gem-austritt-frage">
              <button
                type="button"
                className="o-btn o-btn-primary"
                id="gem-austritt-los"
                disabled={irgendwas}
                onClick={() =>
                  void tun('austritt', async () => {
                    const { hinweis } = await api.dachAustritt(undefined, uuidv7())
                    notify.ok(hinweis)
                    setAustrittFrage(false)
                  })
                }
              >
                {wartet('austritt') ? 'Wird beendet …' : `Aus ${dach.name} austreten`}
              </button>
              <button
                type="button"
                className="o-btn o-btn-ghost"
                id="gem-austritt-abbruch"
                onClick={() => setAustrittFrage(false)}
              >
                Abbrechen
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  )
}

/**
 * Das Formular für die gemeinsamen Angaben.
 *
 * Einmal geschrieben, zweimal benutzt: beim Einrichten und beim Ändern. Eine
 * zweite Fassung wäre eine zweite Stelle, an der ein Feld fehlt.
 */
function Stammdaten({
  form,
  setForm,
  vollstaendig,
  knopf,
  id,
  wartet,
  gesperrt,
  aufSenden,
  aufAbbrechen
}: {
  form: Dacheingabe
  setForm: (f: Dacheingabe) => void
  vollstaendig: boolean
  knopf: string
  id: string
  wartet: boolean
  gesperrt: boolean
  aufSenden: () => void
  aufAbbrechen?: () => void
}): JSX.Element {
  return (
    <div id={id}>
      <div className="o-grid o-grid-2">
        <label className="o-feld">
          <span>Kita-Name</span>
          <input
            id={`${id}-name`}
            autoComplete="off"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </label>
        <label className="o-feld">
          <span>Organisationsform</span>
          <select
            id={`${id}-rechtsform`}
            value={form.rechtsform}
            onChange={(e) => setForm({ ...form, rechtsform: e.target.value as Rechtsform })}
          >
            <option value="selbstaendig">Selbstständige Inhaberinnen</option>
            <option value="gemeinsame_firma">Eine gemeinsame Firma</option>
          </select>
        </label>
        <label className="o-feld">
          <span>Strasse und Nummer</span>
          <input
            id={`${id}-street`}
            autoComplete="off"
            value={form.street}
            onChange={(e) => setForm({ ...form, street: e.target.value })}
          />
        </label>
        <label className="o-feld">
          <span>PLZ</span>
          <input
            id={`${id}-zip`}
            autoComplete="off"
            value={form.zip}
            onChange={(e) => setForm({ ...form, zip: e.target.value })}
          />
        </label>
        <label className="o-feld">
          <span>Ort</span>
          <input
            id={`${id}-city`}
            autoComplete="off"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
          />
        </label>
        <label className="o-feld">
          <span>E-Mail der Kita</span>
          <input
            id={`${id}-email`}
            type="email"
            autoComplete="off"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </label>
        <label className="o-feld">
          <span>Telefon der Kita</span>
          <input
            id={`${id}-phone`}
            autoComplete="off"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </label>
      </div>
      <p className="o-soft small">{RECHTSFORM_TEXT[form.rechtsform]}</p>
      <div className="office-actions">
        <button
          type="button"
          className="o-btn o-btn-primary"
          id={`${id}-senden`}
          disabled={gesperrt || !vollstaendig}
          onClick={aufSenden}
        >
          {wartet ? 'Wird gespeichert …' : knopf}
        </button>
        {aufAbbrechen && (
          <button
            type="button"
            className="o-btn o-btn-ghost"
            id={`${id}-abbruch`}
            onClick={aufAbbrechen}
          >
            Abbrechen
          </button>
        )}
      </div>
    </div>
  )
}
